import { Injectable } from '@angular/core';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import * as _ from 'lodash';

dayjs.extend(isSameOrBefore);
@Injectable()
export class StatsService {
  fillOutData<A extends { time: number; value: number }>(
    sparseDataSets: A[][],
    type: string,
    start: number,
    end: number
  ): {
    times: string[] | number[];
    data: number[][];
  } {
    const sparseDataSetsCopy = _.cloneDeep(sparseDataSets);
    if (type === 'hour') {
      const inc = 60 * 60 * 1000;
      // const sparseDataSetsCopy = _.cloneDeep(sparseDataSets);
      sparseDataSetsCopy.forEach((sparseData) => {
        // order
        sparseData.sort((a, b) => {
          return a.time - b.time;
        });

        const sparseTimes = sparseData.map((x) => x.time);
        const minTime = Math.min(...sparseTimes);
        const maxTime = Math.max(...sparseTimes);
        // Get start time
        start = typeof start === 'number' ? Math.min(start, minTime) : minTime;
        end = typeof end === 'number' ? Math.max(end, maxTime) : maxTime;
      });

      // Create times
      const times: number[] = [];

      for (
        let nthTimeStep = start;
        nthTimeStep <= end;
        nthTimeStep = nthTimeStep + inc
      ) {
        times.push(nthTimeStep);
      }
      // Fill out empty hours
      const data = sparseDataSetsCopy.map((sparseDataSet) => {
        const value: number[] = [];
        times.forEach((nthTimeStep) => {
          const entry = sparseDataSet.find((d) => d.time === nthTimeStep);
          if (entry) {
            value.push(entry.value);
          } else {
            value.push(0);
          }
        });
        return value;
      });

      return { times, data };
    } else if (type === 'Today') {
      const { times, data } = this.getTimeAndDataArray(
        sparseDataSetsCopy,
        start,
        end,
        'hour'
      );
      return { times, data };
    } else if (type === 'Week-Hourly') {
      const { times, data } = this.getTimeAndDataArray(
        sparseDataSetsCopy,
        start,
        end,
        'hour'
      );
      return { times, data };
    } else if (type === 'Week-Daily') {
      // Create Dates
      const times: string[] = [];
      let currentDate = dayjs(start);
      while (currentDate.isSameOrBefore(dayjs(end), 'day')) {
        times.push(dayjs(currentDate).format('YYYY-MM-DD'));
        currentDate = currentDate.add(1, 'days');
      }
      // Fill out empty hours
      const data = sparseDataSetsCopy.map((sparseDataSet) => {
        const value: number[] = [];
        times.forEach((nthTimeStep) => {
          const entry = sparseDataSet.filter((d) =>
            dayjs(d.time)
              .startOf('day')
              .isSame(dayjs(nthTimeStep).startOf('day'))
          );
          if (entry.length > 0) {
            const sum = entry.reduce(function (total, e) {
              return total + e.value;
            }, 0);
            value.push(sum);
          } else {
            value.push(0);
          }
        });
        return value;
      });

      return { times, data };
    } else if (type === 'Past 4 weeks') {
      // Get start time
      // Create Dates
      const times: string[] = [];
      let currentDate = dayjs(start);
      while (currentDate.isSameOrBefore(dayjs(end))) {
        times.push(dayjs(currentDate).format('YYYY-MM-DD'));
        currentDate = currentDate.add(1, 'days');
      }
      // Fill out empty hours
      const data = sparseDataSetsCopy.map((sparseDataSet) => {
        const value: number[] = [];
        times.forEach((nthTimeStep) => {
          const entry = sparseDataSet.filter((d) =>
            dayjs(d.time)
              .startOf('day')
              .isSame(dayjs(nthTimeStep).startOf('day'))
          );
          if (entry.length > 0) {
            const sum = entry.reduce(function (total, e) {
              return total + e.value;
            }, 0);
            value.push(sum);
          } else {
            value.push(0);
          }
        });
        return value;
      });

      return { times, data };
    } else if (type === 'Year') {
      const times: string[] = [];

      let currentDate = dayjs(start);
      while (currentDate.isSameOrBefore(dayjs(end))) {
        times.push(dayjs(currentDate).format('YYYY-MM-DD'));
        currentDate = currentDate.add(1, 'month');
      }

      const data = sparseDataSetsCopy.map((sparseDataSet) => {
        const value: number[] = [];
        times.forEach((nthTimeStep) => {
          const entry = sparseDataSet.filter(
            (d) =>
              dayjs(d.time).get('month') === dayjs(nthTimeStep).get('month') &&
              dayjs(d.time).get('year') === dayjs(nthTimeStep).get('year')
          );
          if (entry.length > 0) {
            const sum = entry.reduce(function (total, e) {
              return total + e.value;
            }, 0);
            value.push(sum);
          } else {
            value.push(0);
          }
        });
        return value;
      });

      return { times, data };
    } else {
      throw new Error('type in fillOutData not found. Try "hour"');
    }
  }

  generateTimeLabels(
    times: string[] | number[],
    type: 'hour' | 'day' | 'month'
  ): string[] {
    if (type === 'hour') {
      return times.map((ele) => {
        return dayjs(ele).format('MMM DD HH:mm');
      });
    } else if (type === 'day') {
      return times.map((ele) => {
        return dayjs(ele).format('MMM DD');
      });
    } else if (type === 'month') {
      return times.map((ele) => {
        return dayjs(ele).format('MMM YYYY');
      });
    } else {
      throw new Error('unknown type');
    }
  }

  getTimeAndDataArray<T extends { time: number; value: number }>(
    sparseDataSets: T[][],
    startTime: number,
    endTime: number,
    selectedFormat: string
  ): {
    times: number[];
    data: number[][];
  } {
    if (selectedFormat !== 'hour')
      throw new Error(
        "Formats which are not 'hour' are not currently implemented"
      );

    const times: number[] = [];
    sparseDataSets.forEach((sparseData) => {
      // order
      sparseData.sort((a, b) => {
        return a.time - b.time;
      });
      const sparseTimes = sparseData.map((x) => x.time);
      const minTime = Math.min(...sparseTimes);
      const maxTime = Math.max(...sparseTimes);

      // Get start time
      startTime =
        typeof startTime === 'number' ? Math.min(startTime, minTime) : minTime;
      endTime =
        typeof endTime === 'number' ? Math.max(endTime, maxTime) : maxTime;
    });

    // Create times
    for (
      let nthTimeStep = startTime;
      nthTimeStep <= endTime;
      nthTimeStep = dayjs(nthTimeStep).add(1, 'hours').valueOf()
    ) {
      times.push(nthTimeStep);
    }

    // Fill out empty hours
    const data = sparseDataSets.map((sparseDataSet) => {
      const value: number[] = [];

      let previousHourTimeStep = dayjs(startTime).valueOf();
      times.forEach((nthTimeStep) => {
        const entry = sparseDataSet.filter(
          (d) =>
            dayjs(d.time).valueOf() > previousHourTimeStep &&
            dayjs(d.time).valueOf() <= dayjs(nthTimeStep).valueOf()
        );
        if (entry.length > 0) {
          const sum = entry.reduce(function (total, e) {
            return total + e.value;
          }, 0);
          value.push(sum);
        } else {
          value.push(0);
        }
        previousHourTimeStep = dayjs(nthTimeStep).valueOf();
      });
      return value;
    });
    return { times, data };
  }
}
